import React, { useState } from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import { DashboardBusiness, useDashboardEmployees } from '~/hooks/apis/business-traveller/useBusinessDashboard';
import { useEmployeeSocieteStatuses } from '~/hooks/apis/business-traveller/useSocieteProgress';

import {
  SocieteEmployeeFilters,
  hasSocieteEmployeeFilters,
  matchesSocieteEmployeeFilters,
} from '~/utils/businessTraveller/societeFilters';

import BusinessDashboardEmployeeRow from './BusinessDashboardEmployeeRow';

interface Props {
  business: DashboardBusiness;
  filters: SocieteEmployeeFilters;
  employeeIds?: Array<string>;
  searchEmployeeIds?: Array<string>;
}

export default function BusinessDashboardEmployees({ business, filters, employeeIds, searchEmployeeIds }: Props) {
  const [page, setPage] = useState(0);
  const { data, isLoading, isError } = useDashboardEmployees(business.id, true);
  const liveEmployees = new Map(data?.map((employee) => [employee.id, employee]));
  const snapshotEmployees = new Map(business.employees.map((employee) => [employee.id, employee]));
  const ids = Array.from(new Set([...liveEmployees.keys(), ...snapshotEmployees.keys()])).sort((a, b) => {
    const ttvA = snapshotEmployees.get(a)?.ttv ?? -Infinity;
    const ttvB = snapshotEmployees.get(b)?.ttv ?? -Infinity;
    return ttvA === ttvB ? 0 : ttvB - ttvA;
  });
  const filtersActive = employeeIds === undefined && hasSocieteEmployeeFilters(filters);
  const customerIdFor = (id: string) =>
    liveEmployees.get(id)?.creditCustomerId || snapshotEmployees.get(id)?.credit_customer_id;
  const customerIds = Array.from(new Set(ids.map(customerIdFor).filter((id): id is string => !!id)));
  const statuses = useEmployeeSocieteStatuses(customerIds, filtersActive);
  const currentTiers = new Map(
    statuses.map(({ customerId, query }) => [customerId, query.isSuccess ? (query.data?.tier ?? null) : undefined]),
  );
  const tierFor = (id: string) => currentTiers.get(customerIdFor(id));
  const filteredIds = ids.filter(
    (id) =>
      (searchEmployeeIds === undefined || searchEmployeeIds.includes(id)) &&
      (employeeIds !== undefined ? employeeIds.includes(id) : matchesSocieteEmployeeFilters(tierFor(id), filters)),
  );
  const checkingStatuses = filtersActive && statuses.some(({ query }) => query.isPending);
  const unavailableStatuses = filtersActive && ids.some((id) => tierFor(id) === undefined);
  const visiblePage = Math.min(page, Math.max(0, Math.ceil(filteredIds.length / 10) - 1));
  if (isLoading) {
    return <CircularProgress size={24} />;
  }
  if (isError) {
    return (
      <Alert severity="error">Unable to load employee names from production. Try expanding the business again.</Alert>
    );
  }
  if (checkingStatuses) {
    return (
      <Box p={2}>
        <CircularProgress size={24} aria-label="Checking employee Société statuses" />
      </Box>
    );
  }
  return (
    <Box p={2} bgcolor="background.default">
      <Typography variant="h6" mb={1}>
        Employees · {business.name}
      </Typography>
      {unavailableStatuses && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Some employees’ current Société status is unavailable. They are excluded from the selected filters.
        </Alert>
      )}
      <Table size="small" aria-label={`${business.name} employees`}>
        <TableHead>
          <TableRow>
            {[
              'Employee',
              'TTV (AUD)',
              'Margin (AUD)',
              'Orders',
              'Société status',
              'Silver granted by LEBT',
              'Silver grant date / progress',
            ].map((label) => (
              <TableCell key={label}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredIds.slice(visiblePage * 10, visiblePage * 10 + 10).map((id) => (
            <BusinessDashboardEmployeeRow
              key={id}
              businessId={business.id}
              employee={liveEmployees.get(id)}
              metrics={snapshotEmployees.get(id)}
              currentTier={filtersActive ? tierFor(id) : undefined}
            />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={filteredIds.length}
        page={visiblePage}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        onPageChange={(_, next) => setPage(next)}
      />
      {!filteredIds.length && (
        <Typography p={2}>
          {filtersActive || searchEmployeeIds !== undefined
            ? 'No employees match the selected filters.'
            : 'No employees registered.'}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Employee financials are attributed to the booking employee in LEBT. Business totals also include orders without
        an employee mapping. Tier progress uses qualifying completed travel in the current membership year, rather than
        lifetime orders. Silver grants are confirmed successful LEBT upgrades from the business activity log; the date
        is the latest recorded Silver grant (Sydney time).
      </Typography>
    </Box>
  );
}
